"use client";

import Link from "next/link";
import { trackProductEvent } from "@/lib/analytics/product";
import React from "react";

type StepLinkProps = {
  href: string;
  stepKey: string;
  action: "do" | "watch";
  className?: string;
  /** Runs after tracking, when the link is followed. */
  onNavigate?: () => void;
  children: React.ReactNode;
};

/**
 * Link used by Get Started step CTAs. Captures `get_started_step_clicked`
 * and falls back to a native anchor for same-page hashes (e.g. `#contact`)
 * so the browser scrolls smoothly to the section.
 */
export default function StepLink({
  href,
  stepKey,
  action,
  className,
  onNavigate,
  children,
}: StepLinkProps) {
  const track = () => {
    trackProductEvent("get_started_step_clicked", { key: stepKey, action });
    onNavigate?.();
  };

  if (href.startsWith("#")) {
    return (
      <a
        href={href}
        className={className}
        onClick={(event) => {
          track();
          const target = document.getElementById(href.slice(1));
          if (target) {
            event.preventDefault();
            target.scrollIntoView({ behavior: "smooth", block: "start" });
            target.focus({ preventScroll: true });
            window.history.replaceState(null, "", href);
          }
        }}>
        {children}
      </a>
    );
  }

  return (
    <Link href={href} className={className} onClick={track}>
      {children}
    </Link>
  );
}
